/**
 * WordTemplateProcessor.js
 * Advanced Microsoft Word & Office HTML Template Processing & Layout Preservation Engine.
 * 
 * Specifically designed for Medical Report Editors (Radiology, Pathology, Clinical Diagnostics)
 * to preserve:
 * - Table structures, rows, columns, and proportional column widths
 * - Merged cells (rowspan / colspan) and nested tables
 * - Exact border widths, styles (solid, dashed, dotted, none), and colors
 * - Cell backgrounds and shading
 * - Cell padding, vertical alignment, and text alignment
 * - Typography (font-family, font-size, bold, italic, underline, strike, colors)
 * - Medical placeholders (e.g. {{PATIENT_NAME}}, {{AGE}}, {{GENDER}}, {{DATE}})
 * - Security sanitization (zero XSS, strips dangerous tags/attributes while preserving visual design)
 */

// Whitelist of clean, universally supported fonts
const ALLOWED_FONTS = [
  'calibri', 'arial', 'times new roman', 'segoe ui', 'georgia',
  'verdana', 'tahoma', 'trebuchet ms', 'helvetica', 'cambria',
  'garamond', 'courier new', 'roboto', 'inter', 'sans-serif', 'serif',
  // Current Office defaults + common clinical-document fonts (root cause 2 fix)
  'aptos', 'aptos display', 'aptos narrow', 'calibri light',
  'liberation serif', 'liberation sans', 'noto sans', 'noto serif',
  'cambria math', 'consolas', 'lato', 'open sans', 'source sans pro'
];

/**
 * Convert any CSS dimension unit (pt, in, cm, mm, pc, px) to numerical pixels
 */
export function unitToPx(value) {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  
  const str = String(value).trim().toLowerCase();
  const num = parseFloat(str);
  if (isNaN(num)) return 0;

  if (str.endsWith('pt')) return num * 1.3333; // 1pt = 96/72 px
  if (str.endsWith('in')) return num * 96;
  if (str.endsWith('cm')) return num * 37.795;
  if (str.endsWith('mm')) return num * 3.7795;
  if (str.endsWith('pc')) return num * 16;
  if (str.endsWith('px')) return num;
  if (str.endsWith('%')) return num; // percentage remains percentage
  return num;
}

/**
 * Checks if raw HTML originated from Microsoft Word or Office applications
 */
export function isWordHtml(html) {
  if (!html || typeof html !== 'string') return false;
  return (
    html.includes('urn:schemas-microsoft-com:office:word') ||
    html.includes('urn:schemas-microsoft-com:office:office') ||
    html.includes('xmlns:w="urn:schemas-microsoft-com:office:word"') ||
    html.includes('ProgId="Word.Document"') ||
    html.includes('class="Mso') ||
    html.includes('class=Mso') ||
    html.includes('mso-') ||
    html.includes('<w:WordDocument>') ||
    /<!--\[if\s+gte?\s+mso/i.test(html)
  );
}

/**
 * Extract CSS class rules from embedded <style> tags before they are stripped
 * Returns a map of className -> style declarations
 */
function extractStyleClassRules(rawHtml) {
  const classRules = {};
  const styleTagRegex = /<style[\s\S]*?>([\s\S]*?)<\/style>/gi;
  let match;

  while ((match = styleTagRegex.exec(rawHtml)) !== null) {
    const cssText = match[1];
    // Simple CSS parser for class selectors: .ClassName { prop: val; ... }
    const ruleRegex = /\.([a-zA-Z0-9_-]+)\s*\{([^}]+)\}/gi;
    let ruleMatch;
    while ((ruleMatch = ruleRegex.exec(cssText)) !== null) {
      const className = ruleMatch[1].trim();
      const declarations = ruleMatch[2].trim();
      if (className && declarations) {
        classRules[className] = (classRules[className] ? classRules[className] + '; ' : '') + declarations;
      }
    }
  }

  return classRules;
}

/**
 * Main Word Template Processor Engine
 */
class WordTemplateProcessorEngine {
  /**
   * Process raw HTML from clipboard and return clean, layout-preserved HTML + stats
   */
  process(rawHtml) {
    if (!rawHtml || typeof rawHtml !== 'string') {
      return { html: '', stats: { isWord: false, tablesCount: 0, rowsCount: 0, colsCount: 0, mergedCellsCount: 0, placeholdersCount: 0 } };
    }

    const isFromWord = isWordHtml(rawHtml);
    let html = rawHtml;

    // 1. Extract style rules from <style> blocks before stripping them
    const classRules = extractStyleClassRules(html);

    // 2. Remove XML tags, namespace declarations and conditional comments
    html = html.replace(/<!--[\s\S]*?-->/gi, '');
    html = html.replace(/<\\?\?xml[^>]*>/gi, '');
    html = html.replace(/<\/?(?:o|w|m|v|x):[^>]*>/gi, '');
    html = html.replace(/<style[\s\S]*?<\/style>/gi, '');
    html = html.replace(/<link[\s\S]*?>/gi, '');

    // 3. Parse with DOMParser
    let doc;
    try {
      const parser = new DOMParser();
      doc = parser.parseFromString(html, 'text/html');
    } catch (e) {
      console.warn('[WordTemplateProcessor] DOMParser failed, using fallback:', e);
      return { html: rawHtml, stats: { isWord: isFromWord, tablesCount: 0, rowsCount: 0, colsCount: 0, mergedCellsCount: 0, placeholdersCount: 0 } };
    }

    const body = doc.body;
    if (!body) {
      return { html: '', stats: { isWord: isFromWord, tablesCount: 0, rowsCount: 0, colsCount: 0, mergedCellsCount: 0, placeholdersCount: 0 } };
    }

    // 4. Statistics tracker
    const stats = {
      isWord: isFromWord,
      tablesCount: 0,
      rowsCount: 0,
      colsCount: 0,
      mergedCellsCount: 0,
      placeholdersCount: 0
    };

    // 5. Apply extracted class rules onto elements before stripping class names
    if (Object.keys(classRules).length > 0) {
      this.applyClassRulesToElements(body, classRules);
    }

    // 6. Normalize all tables (column proportions, borders, shading, merged cells)
    const tables = Array.from(body.querySelectorAll('table'));
    stats.tablesCount = tables.length;
    for (const table of tables) {
      this.normalizeTable(table, stats);
    }

    // 7. Normalize typography, paragraphs, lists, and placeholders
    this.normalizeTypographyAndNodes(body, stats);

    // 8. Clean consecutive blank paragraphs (keep maximum 1 empty line)
    let cleanedHtml = body.innerHTML;
    cleanedHtml = cleanedHtml.replace(/(?:<p[^>]*>\s*(?:&nbsp;|<br\s*\/?>|\s)*<\/p>\s*){3,}/gi, '<p><br></p>');

    return {
      html: cleanedHtml.trim(),
      stats
    };
  }

  /**
   * Copies declarations from extracted <style> classes onto element inline styles
   */
  applyClassRulesToElements(root, classRules) {
    const elementsWithClass = root.querySelectorAll('[class]');
    for (const el of elementsWithClass) {
      const classes = (el.className || '').split(/\s+/);
      const appliedStyles = [];

      for (const cls of classes) {
        if (classRules[cls]) {
          appliedStyles.push(classRules[cls]);
        }
      }

      if (appliedStyles.length > 0) {
        const existingStyle = el.getAttribute('style') || '';
        el.setAttribute('style', (appliedStyles.join('; ') + '; ' + existingStyle).trim());
      }
    }
  }

  /**
   * Deep Table Normalization:
   * - Calculates total table width and column proportions
   * - Sets table-layout: fixed to lock cell proportions
   * - Preserves rowspan & colspan
   * - Normalizes borders, shading, vertical alignment, and cell padding
   */
  normalizeTable(table, stats) {
    // Collect rows
    const rows = Array.from(table.querySelectorAll('tr'));
    stats.rowsCount += rows.length;

    if (rows.length === 0) return;

    // Detect maximum column count across all rows taking colspan into account
    let maxCols = 0;
    const rowColCounts = rows.map(tr => {
      const cells = Array.from(tr.querySelectorAll('th, td'));
      let count = 0;
      cells.forEach(cell => {
        const cs = parseInt(cell.getAttribute('colspan') || '1', 10);
        count += (isNaN(cs) || cs < 1) ? 1 : cs;
      });
      if (count > maxCols) maxCols = count;
      return count;
    });

    if (maxCols > stats.colsCount) {
      stats.colsCount = maxCols;
    }

    // Check for column widths in first row or colgroup
    const colWidths = new Array(maxCols).fill(0);
    const colWidthUnits = new Array(maxCols).fill('px');
    let hasExplicitColWidths = false;

    // 1. Check <colgroup> or <col> elements if present
    const colElements = Array.from(table.querySelectorAll('col'));
    if (colElements.length > 0) {
      colElements.forEach((col, idx) => {
        if (idx < maxCols) {
          const w = col.getAttribute('width') || col.style.width;
          if (w) {
            colWidths[idx] = unitToPx(w);
            hasExplicitColWidths = true;
          }
        }
      });
    }

    // 2. Check cells in rows without colspans to extract column widths
    for (const tr of rows) {
      const cells = Array.from(tr.querySelectorAll('th, td'));
      let colIdx = 0;
      cells.forEach(cell => {
        const cs = parseInt(cell.getAttribute('colspan') || '1', 10);
        const isMerged = (!isNaN(cs) && cs > 1);
        
        // Extract width from style or attribute
        const cellStyle = cell.getAttribute('style') || '';
        const widthMatch = cellStyle.match(/(?:^|;)\s*width\s*:\s*([^;]+)/i);
        const rawWidth = widthMatch ? widthMatch[1].trim() : cell.getAttribute('width');

        if (rawWidth && !isMerged && colIdx < maxCols) {
          const pxVal = unitToPx(rawWidth);
          if (pxVal > 0 && colWidths[colIdx] === 0) {
            colWidths[colIdx] = pxVal;
            hasExplicitColWidths = true;
          }
        }

        colIdx += (isNaN(cs) || cs < 1) ? 1 : cs;
      });
    }

    // Calculate proportional column percentages
    let totalComputedWidth = colWidths.reduce((acc, w) => acc + w, 0);
    const colPercentages = new Array(maxCols).fill(0);

    if (hasExplicitColWidths && totalComputedWidth > 0) {
      for (let i = 0; i < maxCols; i++) {
        colPercentages[i] = colWidths[i] > 0 
          ? Math.round((colWidths[i] / totalComputedWidth) * 1000) / 10 
          : Math.round((100 / maxCols) * 10) / 10;
      }
      // Ensure sum is 100%
      const sumPct = colPercentages.reduce((a, b) => a + b, 0);
      if (sumPct !== 100 && colPercentages.length > 0) {
        colPercentages[colPercentages.length - 1] = Math.round((colPercentages[colPercentages.length - 1] + (100 - sumPct)) * 10) / 10;
      }
    } else {
      // Equal distribution fallback
      const eqPct = Math.round((100 / maxCols) * 10) / 10;
      for (let i = 0; i < maxCols; i++) {
        colPercentages[i] = eqPct;
      }
    }

    // 3. Process every cell in every row
    rows.forEach((tr, rowIdx) => {
      // Clean row styles
      this.normalizeTableRow(tr);

      const cells = Array.from(tr.querySelectorAll('th, td'));
      let colPointer = 0;

      cells.forEach(cell => {
        const cs = parseInt(cell.getAttribute('colspan') || '1', 10);
        const rs = parseInt(cell.getAttribute('rowspan') || '1', 10);

        if ((!isNaN(cs) && cs > 1) || (!isNaN(rs) && rs > 1)) {
          stats.mergedCellsCount++;
        }

        // Clean cell styles, borders, shading, padding, alignment
        this.normalizeTableCell(cell, colPointer, cs, colPercentages);

        colPointer += (isNaN(cs) || cs < 1) ? 1 : cs;
      });
    });

    // 4. Set Master Table Styles (Preserve layout, lock proportions)
    const existingTableStyle = table.getAttribute('style') || '';
    const tableAlign = table.getAttribute('align') || (existingTableStyle.includes('margin: auto') || existingTableStyle.includes('margin:0 auto') ? 'center' : 'left');

    const tableStyles = [
      'width: 100%',
      'border-collapse: collapse',
      'table-layout: fixed',
      'margin: 10px 0',
      'font-size: 13px',
      'line-height: 1.5'
    ];

    if (tableAlign === 'center') {
      tableStyles.push('margin-left: auto', 'margin-right: auto');
    }

    // Preserve table background if present
    const tableBg = this.extractBackground(existingTableStyle) || table.getAttribute('bgcolor');
    if (tableBg) {
      tableStyles.push(`background-color: ${tableBg}`);
    }

    // Preserve table outer border
    const tableBorder = this.extractBorder(existingTableStyle) || (table.getAttribute('border') === '1' ? '1px solid #cbd5e1' : null);
    if (tableBorder && tableBorder !== 'none') {
      tableStyles.push(`border: ${tableBorder}`);
    }

    table.setAttribute('style', tableStyles.join('; '));
    table.removeAttribute('border');
    table.removeAttribute('cellspacing');
    table.removeAttribute('cellpadding');
    table.removeAttribute('width');
    table.removeAttribute('class');
  }

  /**
   * Normalizes a <tr> element
   */
  normalizeTableRow(tr) {
    const style = tr.getAttribute('style') || '';
    const keepStyles = [];

    // Row height
    const heightMatch = style.match(/(?:^|;)\s*height\s*:\s*([^;]+)/i);
    if (heightMatch) {
      const pxH = unitToPx(heightMatch[1]);
      if (pxH > 0) keepStyles.push(`min-height: ${pxH}px`);
    }

    // Page break inside
    if (style.includes('page-break-inside: avoid') || style.includes('page-break-inside:avoid')) {
      keepStyles.push('page-break-inside: avoid');
    }

    if (keepStyles.length > 0) {
      tr.setAttribute('style', keepStyles.join('; '));
    } else {
      tr.removeAttribute('style');
    }

    tr.removeAttribute('class');
  }

  /**
   * Normalizes a <td> or <th> cell
   */
  normalizeTableCell(cell, colIdx, colSpan, colPercentages) {
    const rawStyle = cell.getAttribute('style') || '';
    const cleanStyles = [];

    // 1. Column Width: Calculate percentage based on column span
    const span = (isNaN(colSpan) || colSpan < 1) ? 1 : colSpan;
    let computedWidthPct = 0;
    for (let c = 0; c < span && (colIdx + c) < colPercentages.length; c++) {
      computedWidthPct += colPercentages[colIdx + c];
    }
    if (computedWidthPct > 0) {
      cleanStyles.push(`width: ${Math.round(computedWidthPct * 10) / 10}%`);
    }

    // 2. Borders: extract comprehensive borders
    const borderTop = this.extractSpecificBorder(rawStyle, 'top') || this.extractBorder(rawStyle) || '1px solid #cbd5e1';
    const borderRight = this.extractSpecificBorder(rawStyle, 'right') || this.extractBorder(rawStyle) || '1px solid #cbd5e1';
    const borderBottom = this.extractSpecificBorder(rawStyle, 'bottom') || this.extractBorder(rawStyle) || '1px solid #cbd5e1';
    const borderLeft = this.extractSpecificBorder(rawStyle, 'left') || this.extractBorder(rawStyle) || '1px solid #cbd5e1';

    // Apply clean CSS border rules
    if (borderTop === borderRight && borderRight === borderBottom && borderBottom === borderLeft) {
      cleanStyles.push(`border: ${borderTop}`);
    } else {
      cleanStyles.push(`border-top: ${borderTop}`);
      cleanStyles.push(`border-right: ${borderRight}`);
      cleanStyles.push(`border-bottom: ${borderBottom}`);
      cleanStyles.push(`border-left: ${borderLeft}`);
    }

    // 3. Cell Background / Shading
    const cellBg = this.extractBackground(rawStyle) || cell.getAttribute('bgcolor');
    if (cellBg && cellBg !== 'transparent' && cellBg !== 'none') {
      cleanStyles.push(`background-color: ${cellBg}`);
    }

    // 4. Vertical Alignment (defaults to top for medical reports)
    const vAlign = cell.getAttribute('valign') || this.extractStyleProp(rawStyle, 'vertical-align') || 'top';
    cleanStyles.push(`vertical-align: ${vAlign}`);

    // 5. Horizontal Text Alignment
    const hAlign = cell.getAttribute('align') || this.extractStyleProp(rawStyle, 'text-align');
    if (hAlign && ['left', 'center', 'right', 'justify'].includes(hAlign.toLowerCase())) {
      cleanStyles.push(`text-align: ${hAlign.toLowerCase()}`);
    }

    // 6. Cell Padding (defaults to clean 6px 10px)
    const padding = this.extractStyleProp(rawStyle, 'padding');
    if (padding) {
      cleanStyles.push(`padding: ${this.normalizePadding(padding)}`);
    } else {
      cleanStyles.push('padding: 6px 10px');
    }

    // 7. Word Break / Wrapping
    cleanStyles.push('word-break: break-word');

    cell.setAttribute('style', cleanStyles.join('; '));
    cell.removeAttribute('class');
    cell.removeAttribute('width');
    cell.removeAttribute('height');
    cell.removeAttribute('valign');
    cell.removeAttribute('align');
    cell.removeAttribute('bgcolor');

    // Keep colspan & rowspan if > 1
    if (span <= 1) cell.removeAttribute('colspan');
    const rs = parseInt(cell.getAttribute('rowspan') || '1', 10);
    if (isNaN(rs) || rs <= 1) cell.removeAttribute('rowspan');
  }

  /**
   * Normalizes paragraph and inline typography across the document
   */
  normalizeTypographyAndNodes(rootNode, stats) {
    const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_ELEMENT);
    const elementsToProcess = [];

    while (walker.nextNode()) {
      elementsToProcess.push(walker.currentNode);
    }

    for (const el of elementsToProcess) {
      const tagName = el.tagName.toLowerCase();

      // Remove unwanted script, meta, style, iframe, object, embed, svg (svg can carry
      // <script>/onload payloads that don't fit neatly into the per-attribute checks below)
      if (['script', 'style', 'meta', 'link', 'object', 'embed', 'applet', 'iframe', 'svg'].includes(tagName)) {
        if (el.parentNode) el.parentNode.removeChild(el);
        continue;
      }

      // Security: Strip dangerous on* event attributes and javascript: URIs (incl. xlink:href)
      // from all elements. Whitespace/control characters are stripped from the value before the
      // colon-prefix check so obfuscated variants like "java\tscript:" / "java\nscript:" can't
      // slip past a plain trim+lowercase comparison.
      const attrs = Array.from(el.attributes || []);
      for (const attr of attrs) {
        const attrName = attr.name.toLowerCase();
        if (attrName.startsWith('on')) {
          el.removeAttribute(attr.name);
        } else if (attrName === 'href' || attrName === 'src' || attrName === 'xlink:href') {
          // eslint-disable-next-line no-control-regex -- intentionally matching control chars to de-obfuscate "java\tscript:"-style URIs
          const deObfuscated = attr.value.replace(/[\s\x00-\x1F\x7F]+/g, '').toLowerCase();
          if (deObfuscated.startsWith('javascript:')) {
            el.removeAttribute(attr.name);
          }
        }
      }

      // Unwrap Microsoft <font> tags into clean spans
      if (tagName === 'font') {
        const face = el.getAttribute('face');
        const size = el.getAttribute('size');
        const color = el.getAttribute('color');
        const span = document.createElement('span');
        const styles = [];

        if (face) {
          const cleanFace = face.toLowerCase();
          if (ALLOWED_FONTS.some(f => cleanFace.includes(f))) {
            styles.push(`font-family: ${face}`);
          } else {
            // Same serif/sans-serif fallback heuristic as cleanGeneralElement (root cause 2)
            styles.push(`font-family: ${this.genericFontFallback(cleanFace)}`);
          }
        }
        if (color) styles.push(`color: ${color}`);
        if (size) {
          const sizePxMap = { '1': '10px', '2': '12px', '3': '14px', '4': '16px', '5': '18px', '6': '24px', '7': '32px' };
          if (sizePxMap[size]) styles.push(`font-size: ${sizePxMap[size]}`);
        }

        if (styles.length > 0) span.setAttribute('style', styles.join('; '));
        while (el.firstChild) span.appendChild(el.firstChild);
        if (el.parentNode) el.parentNode.replaceChild(span, el);
        continue;
      }

      // Check text nodes inside element for placeholders
      this.detectPlaceholdersInElement(el, stats);

      // Clean inline attributes and styles
      if (!['table', 'tr', 'td', 'th'].includes(tagName)) {
        this.cleanGeneralElement(el);
      }
    }
  }

  /**
   * Cleans general typography elements (p, div, span, h1-h6, b, i, u, s, li)
   */
  cleanGeneralElement(el) {
    const rawStyle = el.getAttribute('style');
    const tagName = el.tagName.toLowerCase();
    const keepStyles = [];

    // 1. Text Alignment — read the `align` attribute OR the inline style (same precedence as
    // normalizeTableCell's hAlign, which already gets this right: `align` attr first, falling
    // back to inline `text-align`). This must run even when there is no `style` attribute at all,
    // since Word/Outlook frequently express paragraph alignment via `align="center"` alone
    // (root cause 1 — this was previously only read from rawStyle and the `align` attribute was
    // silently discarded below without ever being consulted).
    const hAlign = el.getAttribute('align') || this.extractStyleProp(rawStyle, 'text-align');
    if (hAlign && ['left', 'center', 'right', 'justify'].includes(hAlign.toLowerCase())) {
      keepStyles.push(`text-align: ${hAlign.toLowerCase()}`);
    }

    if (rawStyle) {
      // 2. Font Weight
      const fontWeight = this.extractStyleProp(rawStyle, 'font-weight');
      if (fontWeight === 'bold' || parseInt(fontWeight, 10) >= 600) {
        keepStyles.push('font-weight: bold');
      }

      // 3. Font Style
      const fontStyle = this.extractStyleProp(rawStyle, 'font-style');
      if (fontStyle === 'italic' || fontStyle === 'oblique') {
        keepStyles.push('font-style: italic');
      }

      // 4. Text Decoration
      const textDec = this.extractStyleProp(rawStyle, 'text-decoration');
      if (textDec) {
        if (textDec.includes('underline')) keepStyles.push('text-decoration: underline');
        if (textDec.includes('line-through')) keepStyles.push('text-decoration: line-through');
      }

      // 5. Font Size
      const fontSize = this.extractStyleProp(rawStyle, 'font-size');
      if (fontSize) {
        const px = unitToPx(fontSize);
        if (px >= 8 && px <= 48) {
          keepStyles.push(`font-size: ${Math.round(px * 10) / 10}px`);
        }
      }

      // 6. Font Family (if whitelisted, else fall back to a generic serif/sans-serif family
      // instead of dropping the style entirely — root cause 2)
      const fontFamily = this.extractStyleProp(rawStyle, 'font-family');
      if (fontFamily) {
        const cleanFont = fontFamily.replace(/["']/g, '').toLowerCase();
        if (ALLOWED_FONTS.some(f => cleanFont.includes(f))) {
          keepStyles.push(`font-family: ${fontFamily}`);
        } else {
          keepStyles.push(`font-family: ${this.genericFontFallback(cleanFont)}`);
        }
      }

      // 7. Text Color
      const color = this.extractStyleProp(rawStyle, 'color');
      if (color && !color.includes('windowtext') && color !== 'inherit') {
        keepStyles.push(`color: ${color}`);
      }

      // 8. Highlight / Background
      const bg = this.extractBackground(rawStyle);
      if (bg && bg !== 'transparent' && bg !== 'none') {
        keepStyles.push(`background-color: ${bg}`);
      }

      // 9. Line Spacing & Margins (for paragraphs and headings)
      if (['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
        const lineHeight = this.extractStyleProp(rawStyle, 'line-height');
        if (lineHeight) keepStyles.push(`line-height: ${lineHeight}`);

        const marginBottom = this.extractStyleProp(rawStyle, 'margin-bottom');
        if (marginBottom) {
          const mbPx = unitToPx(marginBottom);
          keepStyles.push(`margin-bottom: ${Math.min(mbPx, 24)}px`);
        }

        const marginLeft = this.extractStyleProp(rawStyle, 'margin-left') || this.extractStyleProp(rawStyle, 'text-indent');
        if (marginLeft) {
          const mlPx = unitToPx(marginLeft);
          if (mlPx > 0) keepStyles.push(`margin-left: ${Math.min(mlPx, 60)}px`);
        }
      }
    }

    // Apply or remove style
    if (keepStyles.length > 0) {
      el.setAttribute('style', keepStyles.join('; '));
    } else {
      el.removeAttribute('style');
    }

    // Strip Microsoft classes and proprietary attributes
    el.removeAttribute('class');
    el.removeAttribute('lang');
    el.removeAttribute('dir');
    el.removeAttribute('v:shapes');
    el.removeAttribute('align');
  }

  /**
   * Detects and counts placeholders like {{PATIENT_NAME}}, {{AGE}}, {{GENDER}}, etc.
   */
  detectPlaceholdersInElement(el, stats) {
    if (el.childNodes.length === 1 && el.firstChild.nodeType === Node.TEXT_NODE) {
      const text = el.firstChild.textContent;
      const placeholderMatches = text.match(/\{\{[A-Z0-9_-]+\}\}/g);
      if (placeholderMatches) {
        stats.placeholdersCount += placeholderMatches.length;
      }
    }
  }

  // ── Helper Parsing Functions ──────────────────────────────────────────

  extractStyleProp(styleStr, propName) {
    if (!styleStr) return null;
    const regex = new RegExp(`(?:^|;)\\s*${propName}\\s*:\\s*([^;]+)`, 'i');
    const m = styleStr.match(regex);
    return m ? m[1].trim() : null;
  }

  extractBackground(styleStr) {
    if (!styleStr) return null;
    const bg = this.extractStyleProp(styleStr, 'background-color') || this.extractStyleProp(styleStr, 'background');
    if (!bg) return null;
    // Normalize Word's windowbackground or hex
    if (bg.includes('windowbackground') || bg.includes('windowtext')) return null;
    return bg.trim();
  }

  extractBorder(styleStr) {
    if (!styleStr) return null;
    const b = this.extractStyleProp(styleStr, 'border');
    if (!b) return null;
    if (b.toLowerCase() === 'none' || b === '0') return 'none';
    return this.cleanBorderValue(b);
  }

  extractSpecificBorder(styleStr, side) {
    if (!styleStr) return null;
    const b = this.extractStyleProp(styleStr, `border-${side}`);
    if (!b) return null;
    if (b.toLowerCase() === 'none' || b === '0') return 'none';
    return this.cleanBorderValue(b);
  }

  cleanBorderValue(val) {
    if (!val) return '1px solid #cbd5e1';
    let b = val.trim();
    // Replace windowtext with clean clinical dark slate
    b = b.replace(/windowtext/gi, '#1e293b');
    // Normalize pt to px
    b = b.replace(/([0-9.]+)pt/gi, (_, num) => `${Math.max(1, Math.round(parseFloat(num) * 1.333))}px`);
    return b;
  }

  normalizePadding(val) {
    if (!val) return '6px 10px';
    return val.replace(/([0-9.]+)pt/gi, (_, num) => `${Math.max(2, Math.round(parseFloat(num) * 1.333))}px`)
              .replace(/([0-9.]+)in/gi, (_, num) => `${Math.round(parseFloat(num) * 96)}px`);
  }

  /**
   * Root cause 2 fallback heuristic: a lowercased, unrecognized font name maps to the generic
   * `serif` family only if it contains a known serif keyword (and isn't itself a "sans" variant
   * like "comic sans ms"); everything else defaults to `sans-serif`, since that's both Word's own
   * current default (Aptos) and the common case for clinical report templates.
   */
  genericFontFallback(lowerFontName) {
    if (!lowerFontName) return 'sans-serif';
    if (lowerFontName.includes('sans')) return 'sans-serif';
    const serifHints = [
      'serif', 'times', 'georgia', 'garamond', 'constantia', 'palatino',
      'book antiqua', 'bookman', 'century', 'rockwell', 'didot',
      'baskerville', 'goudy', 'minion', 'caslon', 'cochin', 'perpetua'
    ];
    return serifHints.some(h => lowerFontName.includes(h)) ? 'serif' : 'sans-serif';
  }
}

// Singleton Engine Instance
const wordTemplateProcessor = new WordTemplateProcessorEngine();

export default wordTemplateProcessor;

/**
 * Convenience helper function
 */
export function processWordHtml(html) {
  return wordTemplateProcessor.process(html);
}
